import React, { useState, useEffect } from "react";
import { Navigation, ScanFace, CheckCircle, XCircle, RefreshCw, MapPin, Clock, LogOut } from "lucide-react";
import api from "../../api";
import { K } from "../../theme";
import { Btn, Card, Pill } from "../components";
import { fmtHM, fmtTime, fmtDate } from "../utils";

export const Attendance = ({ me, todayRec, onRefresh }) => {
  const [step, setStep] = useState("idle");
  const [mode, setMode] = useState("checkin");
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);
  const [faceVerified, setFaceVerified] = useState(false);
  const [facePhoto, setFacePhoto] = useState(null);
  const [faceStatus, setFaceStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);

  const hasCheckIn = todayRec && todayRec.check_in;
  const hasCheckOut = todayRec && todayRec.check_out;

  useEffect(() => {
    api.get('/api/attendance/employee/0?year=' + new Date().getFullYear() + '&month=' + (new Date().getMonth() + 1))
      .then(d => setRecent((d?.records || []).slice(0, 7)))
      .catch(() => {});
  }, [todayRec]);

  const reset = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setStep("idle"); setCoords(null); setError(null); setFaceVerified(false); setFacePhoto(null); setFaceStatus(null); setLoading(false); setResult(null);
  };

  const startFlow = (m) => {
    reset();
    setMode(m);
    setStep("gps");
    if (!navigator.geolocation) { setError("GPS not supported on this device"); setStep("error"); return; }
    const onSuccess = async (pos) => {
      const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setCoords(c);
      setStep("loc_check");
      try {
        await api.post(m === "checkout" ? '/api/attendance/checkout' : '/api/attendance/checkin', { latitude: c.lat, longitude: c.lng, precheck: true });
        setStep("face");
      } catch (e) {
        setError(e.message || "Location check failed");
        setStep("error");
      }
    };
    navigator.geolocation.getCurrentPosition(
      onSuccess,
      (err) => {
        if (err.code === err.TIMEOUT) {
          navigator.geolocation.getCurrentPosition(
            onSuccess,
            (e2) => { setError("GPS denied: " + e2.message); setStep("error"); },
            { enableHighAccuracy: false, timeout: 30000, maximumAge: 120000 }
          );
        } else {
          setError("GPS denied: " + err.message); setStep("error");
        }
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 60000 }
    );
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
      setFaceStatus("ready");
    } catch (e) { setError("Camera blocked: " + e.message); setStep("error"); }
  };

  useEffect(() => {
    if (step === "face" && !streamRef.current) startCamera();
    return () => { if (step !== "face" && streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } };
    // eslint-disable-next-line
  }, [step]);

  const captureSelfie = () => {
    if (!videoRef.current) return;
    try {
      const v = videoRef.current;
      const c = document.createElement("canvas"); c.width = v.videoWidth || 640; c.height = v.videoHeight || 480;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      const photo = c.toDataURL("image/jpeg", 0.75);
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
      setFacePhoto(photo); setFaceVerified(true); setFaceStatus("captured"); setError(null);
    } catch (e) { setError("Capture failed: " + (e.message || "")); }
  };

  const submit = async () => {
    setLoading(true); setError(null);
    try {
      const path = mode === "checkout" ? '/api/attendance/checkout' : '/api/attendance/checkin';
      const r = await api.post(path, { latitude: coords?.lat, longitude: coords?.lng, face_photo: facePhoto });
      setResult(r); setStep("done");
      onRefresh && onRefresh();
    } catch (e) {
      if ((e.code === 'FACE_MISMATCH' || (e.message || '').toLowerCase().includes('face')) && (e.message || '').toLowerCase().includes('match')) {
        setFaceVerified(false); setFacePhoto(null); setFaceStatus("mismatch"); setError(e.message);
      } else {
        setError(e.message || "Failed");
      }
      setLoading(false);
    }
  };

  return <div className="px-4 pt-4 pb-[100px]">
    <div className="text-[22px] font-extrabold text-t0 mb-[14px]">Attendance</div>

    {step === "idle" && <div>
      <Card className="mb-4" style={{ background: hasCheckOut ? K.gb : hasCheckIn ? K.blb : K.s, borderColor: hasCheckOut ? K.gd : hasCheckIn ? `${K.bl}30` : K.b0 }}>
        <div className="text-center mb-3">
          {hasCheckOut ? <CheckCircle size={42} color={K.g} className="mx-auto" /> : hasCheckIn ? <Clock size={42} color={K.bl} className="mx-auto" /> : <ScanFace size={42} color={K.t3} className="mx-auto" />}
          <div className="text-[18px] font-bold text-t0 mt-2">{hasCheckOut ? "Done for today" : hasCheckIn ? "You are checked in" : "Not checked in yet"}</div>
        </div>
        {hasCheckIn && <div className="flex justify-around pt-[10px] border-t border-b0">
          <div className="text-center"><div className="text-[14px] text-t3">IN</div><div className="text-[16px] font-bold text-g">{fmtTime(todayRec.check_in)}</div></div>
          <div className="text-center"><div className="text-[14px] text-t3">OUT</div><div className="text-[16px] font-bold" style={{ color: hasCheckOut ? K.bl : K.t3 }}>{hasCheckOut ? fmtTime(todayRec.check_out) : "—"}</div></div>
          <div className="text-center"><div className="text-[14px] text-t3">HOURS</div><div className="text-[16px] font-bold text-t0">{fmtHM(todayRec.work_hours)}</div></div>
        </div>}
      </Card>

      {!hasCheckIn && <Btn full icon={Navigation} onClick={() => startFlow("checkin")}>Check In</Btn>}
      {hasCheckIn && !hasCheckOut && <Btn full icon={LogOut} onClick={() => startFlow("checkout")} style={{ background: K.bl }}>Check Out</Btn>}

      <div className="mt-6">
        <div className="text-[14px] font-bold text-t2 uppercase tracking-[0.05em] mb-[10px]">Recent</div>
        {recent.length === 0 && <Card><div className="text-center text-t3 text-[15px] py-2">No history this month</div></Card>}
        {recent.map(r => <Card key={r.id} className="mb-2" style={{ padding: "10px 14px" }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[15px] font-semibold text-t0">{fmtDate(r.date && r.date.toString().slice(0, 10))}</div>
              <div className="text-[14px] text-t3 mt-[2px]">{fmtTime(r.check_in)} → {fmtTime(r.check_out)}</div>
            </div>
            <div className="text-right">
              <div className="text-[15px] font-bold" style={{ color: r.work_hours >= 8 ? K.g : r.work_hours >= 4 ? K.a : K.r }}>{fmtHM(r.work_hours)}</div>
              <Pill c={r.status === "Present" ? K.g : r.status === "Late" ? K.a : r.status === "WFH" ? K.bl : r.status === "Half Day" ? K.pu : K.r}>{r.status}</Pill>
            </div>
          </div>
        </Card>)}
      </div>
    </div>}

    {step === "gps" && <Card style={{ padding: 36 }}><div className="text-center">
      <Navigation size={48} color={K.bl} className="mx-auto mb-3" />
      <div className="text-base font-bold text-t0">Getting your location…</div>
      <div className="text-[14px] text-t3 mt-[6px]">Please allow location access</div>
    </div></Card>}

    {step === "loc_check" && <Card style={{ padding: 36 }}><div className="text-center">
      <MapPin size={48} color={K.p} className="mx-auto mb-3" />
      <div className="text-base font-bold text-t0">Verifying location…</div>
      {coords && <div className="text-[14px] text-t3 mt-[6px]">📍 {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</div>}
    </div></Card>}

    {step === "face" && <Card><div className="text-center">
      <div className="text-[15px] font-bold text-t0 mb-1">{faceStatus === "captured" ? "Selfie Captured" : "Take Selfie"}</div>
      <div className="text-[14px] text-t3 mb-3">{faceStatus === "captured" ? "Confirm to verify on server" : loading ? "Verifying with server…" : "Center your face in the frame"}</div>
      <div className="relative rounded-[14px] overflow-hidden mx-auto max-w-[320px] bg-black aspect-[4/3]">
        {facePhoto
          ? <img src={facePhoto} alt="selfie" className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
          : <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />}
        <div className="absolute top-[15%] left-[22%] right-[22%] bottom-[15%] rounded-full pointer-events-none border-2 border-dashed" style={{ borderColor: `${K.p}aa` }} />
      </div>
      {error && <div className="p-[10px] bg-rb rounded-lg text-r text-[14px] mt-[10px]">{error}</div>}
      <div className="flex gap-2 mt-[14px]">
        {faceStatus === "mismatch" ? <>
          <Btn full variant="secondary" onClick={() => { setFacePhoto(null); setFaceVerified(false); setFaceStatus(null); setError(null); startCamera(); }} icon={RefreshCw}>Retry Selfie</Btn>
          <Btn full variant="ghost" onClick={reset}>Cancel</Btn>
        </> : faceVerified && facePhoto ? <>
          <Btn full icon={CheckCircle} onClick={submit} disabled={loading}>{loading ? "Verifying…" : "Confirm " + (mode === "checkout" ? "Check Out" : "Check In")}</Btn>
          <Btn variant="ghost" onClick={() => { setFacePhoto(null); setFaceVerified(false); setFaceStatus(null); startCamera(); }} disabled={loading}>Retake</Btn>
        </> : <>
          <Btn full icon={ScanFace} onClick={captureSelfie}>Capture Selfie</Btn>
          <Btn variant="ghost" onClick={reset}>Cancel</Btn>
        </>}
      </div>
    </div></Card>}

    {step === "error" && <Card><div className="text-center">
      <XCircle size={48} color={K.r} className="mx-auto mb-[10px]" />
      <div className="text-base font-bold text-r mb-[6px]">{error?.toLowerCase().includes("location") ? "Location Mismatch" : "Couldn't proceed"}</div>
      <div className="text-[15px] text-t2 mb-[14px] px-2">{error}</div>
      <div className="flex gap-2">
        <Btn full icon={RefreshCw} onClick={() => startFlow(mode)}>Retry</Btn>
        <Btn variant="ghost" onClick={reset}>Cancel</Btn>
      </div>
    </div></Card>}

    {step === "done" && <Card style={{ background: K.gb, borderColor: K.gd }}><div className="text-center">
      <CheckCircle size={56} color={K.g} className="mx-auto mb-[10px]" />
      <div className="text-[18px] font-extrabold text-g mb-[6px]">{mode === "checkout" ? "Checked Out!" : "Checked In!"}</div>
      {result?.geo_message && <div className="text-[14px] text-t2 mb-[6px]">{result.geo_message}</div>}
      {result?.work_hours != null && <div className="text-[15px] text-t0 mb-[6px]">Total: <strong>{fmtHM(result.work_hours)}</strong></div>}
      {result?.warning && <div className="text-[14px] text-a mb-[10px] px-3 py-2 bg-ab rounded-lg">{result.warning}</div>}
      <Btn full variant="secondary" onClick={reset}>Done</Btn>
    </div></Card>}
  </div>;
};
